/** The portal tour's chapters (DESIGN §20.3): what to light up, where, and what to say. */
import type { Me } from "../api/client.js";

export type ChapterID = "plan" | "week" | "athlete" | "lifts";

export interface TourContext {
  me: Me;
  week: string;
  athletes: { id: string; workouts: unknown[] }[];
  /** The first lift on the Lifts page, once it is known. */
  firstLift?: string | null;
  phone: boolean;
}

export interface Step {
  id: string;
  /** Where the step is; a function is read when the step comes, so it sees the team as it is then. */
  path?: string | ((ctx: TourContext) => string | null);
  /** The part to light up: the first of these on screen. None, or none on screen, centres the card. */
  targets: string[];
  title: string;
  body: string;
  /** Said instead of `body` when none of `targets` is on screen. */
  instead?: string;
}

export interface Chapter {
  id: ChapterID;
  title: string;
  steps: Step[];
}

export const CHAPTER_TITLES: Record<ChapterID, string> = {
  plan: "Planning a week",
  week: "Checking the week",
  athlete: "Following an athlete",
  lifts: "Lifts",
};

function planSteps(ctx: TourContext): Step[] {
  const path = `/w/${ctx.week}/plan`;
  const sheet: Step = {
    id: "plan-sheet",
    path,
    targets: [".bp-grid", ".bp-sheet-view", ".bp-empty-week", ".bp-empty"],
    title: "Your week, as your sheet",
    body: "Days down, groups across, written the way you write your sheet. Athletes get it on their phones once you publish it.",
  };
  const weeks: Step = {
    id: "plan-weeks",
    targets: [".bp-week-nav"],
    title: "Moving between weeks",
    body: "‹ and › go back and forward a week; the date opens any week, past or coming.",
  };
  if (ctx.phone)
    return [
      sheet,
      weeks,
      {
        id: "plan-computer",
        targets: [],
        title: "Writing the week",
        body: "On a phone the Plan is to read. Open the portal on a computer to start a week, write its sessions, set who is in each group and publish it.",
      },
    ];
  return [
    sheet,
    weeks,
    {
      id: "plan-start",
      targets: ['[data-tour="start-week"]'],
      title: "Starting a week",
      body: "A new week starts from last week's copy, from blank, or from what was published from the phone.",
      instead: "This week is already started. A new week starts from last week's copy, from blank, or from what was published from the phone.",
    },
    {
      id: "plan-session",
      targets: [".bp-cell-chips", ".bp-sheet-grid .bp-sheet-chips"],
      title: "Writing a session",
      body: "In the editor, click a session to write it in your own words. Beside your text you see what athletes get, line by line, and what they log.",
      instead: "Once the week has sessions, click one to write it in your own words, with what athletes get beside it.",
    },
    {
      id: "plan-definitions",
      targets: ["aside.bp-panel", ".bp-sheet-defs"],
      title: "Definitions",
      body: "What each label in the grid means: Sprints #1, Weights #2… Click one to open it in the editor.",
      instead: "Definitions list what each label in the grid means; they show here once the week is started.",
    },
    {
      id: "plan-groups",
      targets: [".bp-colhead", ".bp-sheet-grid thead"],
      title: "Groups",
      body: "Each column is a group. Who's in it decides which athletes get that column's sessions.",
      instead: "Each column is a group, and who's in it decides which athletes get its sessions.",
    },
    {
      id: "plan-publish",
      targets: ['[data-tour="publish"]'],
      title: "Publishing",
      body: "Until you publish, the week is your draft. Publish sends it to everyone's phone; publishing again updates it.",
      instead: "Until you publish, the week is your draft. Publish sends it to everyone's phone.",
    },
  ];
}

function weekSteps(ctx: TourContext): Step[] {
  return [
    {
      id: "week-grid",
      path: `/w/${ctx.week}`,
      targets: [ctx.phone ? ".wk-phone" : ".wk-matrix-wrap"],
      title: "Everyone, day by day",
      body: "Each athlete's sessions this week, in their group, with what came in.",
      instead: "Once the week is published, each athlete's sessions show here, day by day.",
    },
    {
      id: "week-marks",
      targets: [".wk-pill"],
      title: "What the marks mean",
      body: "Done: a workout came in for it. Not logged: the day has passed and nothing came in, which isn't the same as skipped. Today and Planned are still to come.",
      instead: "Once the week has sessions, each shows Done, Today, Planned or Not logged.",
    },
    {
      id: "week-session",
      targets: [".wk-item"],
      title: "A session's results",
      body: "Click a session to see everyone's sets, times and personal bests, and who hasn't logged it.",
      instead: "Once the week has sessions, each opens to everyone's results.",
    },
    {
      id: "week-notes",
      targets: [".wk-notes"],
      title: "Notes",
      body: "What athletes wrote about their workouts, when they share notes.",
      instead: "Notes athletes share about their workouts show here.",
    },
  ];
}

function athleteSteps(ctx: TourContext): Step[] {
  if (!ctx.athletes.length)
    return [
      {
        id: "athlete-none",
        path: "/athletes",
        targets: [],
        title: "Your athletes",
        body: "Athletes appear here once they join the team with a code from the app.",
      },
    ];
  const pick = (c: TourContext) => {
    const a = c.athletes.find((x) => x.workouts.length > 0) ?? c.athletes[0];
    return a ? "/athlete/" + encodeURIComponent(a.id) : null;
  };
  return [
    {
      id: "athlete-list",
      path: "/athletes",
      targets: [".k-table"],
      title: "Everyone on the team",
      body: "Each athlete's group this week, what they've done, what's not logged and their personal bests. Click a name to open them.",
    },
    {
      id: "athlete-load",
      path: pick,
      targets: ['[aria-labelledby="ath-load-h"]'],
      title: "Load",
      body: "How much they lifted each week, against their usual. A week well above or below it is marked.",
    },
    {
      id: "athlete-week",
      targets: ['[aria-labelledby="ath-week-h"]'],
      title: "This week",
      body: "Each of their sessions this week, done or not logged, with their top sets.",
    },
    {
      id: "athlete-running",
      targets: ['[aria-labelledby="ath-running-h"]'],
      title: "Running",
      body: "Their metres each week by type of run, and their bests: fastest times and top speeds. Point at a week to see its numbers.",
      instead: "Once they log a run, their metres by week and their bests show here.",
    },
    {
      id: "athlete-lifts",
      targets: ["#progress"],
      title: "Lifts",
      body: "Their heaviest set each week for any lift they've done. Pick a lift to see it.",
    },
    {
      id: "athlete-history",
      targets: ['[aria-labelledby="ath-history-h"]'],
      title: "History",
      body: "Every workout they've shared, newest first, each with its sets and notes.",
    },
  ];
}

function liftSteps(): Step[] {
  return [
    {
      id: "lifts-list",
      path: "/lifts",
      targets: ['table[aria-label="Lifts"]', 'input[aria-label="Search lifts"]'],
      title: "Every lift",
      body: "Every lift the team has done, this week's first. Search to find one.",
    },
    {
      id: "lifts-one",
      path: (c) => (c.firstLift ? "/lift/" + encodeURIComponent(c.firstLift) : null),
      targets: [".lift-athletes"],
      title: "A lift, athlete by athlete",
      body: "Each athlete's heaviest set, their best at each rep count, their latest and their last 8 weeks.",
      instead: "Once someone logs a lift, it opens to everyone's sets.",
    },
    {
      id: "lifts-group",
      targets: ['select[aria-label="Column"]'],
      title: "One group at a time",
      body: "Show only one group's athletes.",
      instead: "With more than one group, you can show one group's athletes at a time.",
    },
  ];
}

export function chapters(ctx: TourContext): Chapter[] {
  const out: Chapter[] = [];
  if (ctx.me.person.canPlan) out.push({ id: "plan", title: CHAPTER_TITLES.plan, steps: planSteps(ctx) });
  out.push({ id: "week", title: CHAPTER_TITLES.week, steps: weekSteps(ctx) });
  out.push({ id: "athlete", title: CHAPTER_TITLES.athlete, steps: athleteSteps(ctx) });
  out.push({ id: "lifts", title: CHAPTER_TITLES.lifts, steps: liftSteps() });
  return out;
}
